import type { ValidationErrors } from "final-form";
import { _, _any } from "i18n";
import type { ClipsUtilityProps, GameClipsRowResponse } from "../../../types";
import { useEffect, useState } from "react";
import { Field, Form } from "react-final-form";
import Modal from "ui-kit/src/Modal";
import { useToast } from "ui-kit";
import "./clips.css";

interface ShareClipModalProps {
    toastMessage: (type: string, message: string) => void;
    domainURL: string;
    show?: boolean;
    clip: GameClipsRowResponse;
    newShareSlug: ClipsUtilityProps["newShareSlug"];
    generateShareLink: ClipsUtilityProps["generateShareLink"];
    onClose: () => void;
}

enum InputType {
    Text = "text",
    Password = "password",
}

export function ShareClipModal({ toastMessage, generateShareLink, show = false, clip, onClose, newShareSlug, domainURL }: ShareClipModalProps) {
    const [showPassword, setShowPassword] = useState(false);
    const [isPassEnabled, setIsPassEnabled] = useState(!clip.password);
    const [clipLinkState, setClipLinkState] = useState(clip.shareSlug ? `${domainURL}/clip/${clip.shareSlug}` : "");
    const [isPasswordUpdated, setIsPasswordUpdated] = useState(false);
    const [password, setPassword] = useState(clip.password ? "•••••••" : "");

    useEffect(() => {
        if (newShareSlug.id === clip.id) {
            setClipLinkState(newShareSlug.slug)
        }
    }, [newShareSlug])

    const initData = {
        ...(clip.expireLinkAt && { expireDate: clip.expireLinkAt.slice(0, 10) }),
        ...(password && { password: password }),
        ...(clip.shareSlug && { link: `${domainURL}/clip/${clip.shareSlug}` }),
        ...(clip.description && { description: clip.description }),
    };

    const onSubmit = (values: typeof initData) => {
        generateShareLink(clip.id, values.description, values.password, values.expireDate, isPasswordUpdated);
    };

    const validate = (values: typeof initData): ValidationErrors => {
        const errors: ValidationErrors = {};
        const passwordRegex = /[-_!@#$%^&*(),.?":{}|<>]/;
        if (!clip.password || isPasswordUpdated) {
            if (values.password && !(values.password.length >= 6 && passwordRegex.test(values.password))) {
                errors.password = _("label.password.clip.wrong_format");
            }
        }
        return errors;
    };

    return (
        <div onClick={e => e.stopPropagation()}>
            <Modal show={show} size="sm" title={_("label.share_the_clip")} onClose={() => onClose()}>

                <Form
                    onSubmit={onSubmit}
                    initialValues={initData}
                    validate={validate}
                    render={({ handleSubmit }) => {
                        return (
                            <form className="shareForm-container" onSubmit={handleSubmit}>
                                <div className="formField">
                                    <div className="fieldLabel">
                                        {_("label.link_expire_date")} <br /> <span>
                                            {_("label.set_expiration_date_disable_Link_after_this_date")}
                                        </span>{" "}
                                    </div>
                                    <Field name="expireDate" type="text">
                                        {({ input, meta }) => <input {...input} type="date" required={true} min={new Date().toJSON().slice(0, 10)} />}
                                    </Field>
                                </div>
                                <div className="formField">
                                    <div className="fieldLabel">
                                        {_("label.password_protect")} <br /> <span>{_("label.by_adding_password_you_can_restrict_access")}</span>{" "}
                                    </div>
                                    <Field
                                        name="password"
                                        type={showPassword ? InputType.Text : InputType.Password}
                                        value={password}
                                        onChange={setPassword}
                                    >
                                        {({ input, meta }) => (
                                            <div className="paswordContainer">
                                                <input {...input} required={!clip.password} disabled={!isPassEnabled} />
                                                <div className="togglePassword" id="togglePassword">
                                                    {isPassEnabled ? (
                                                        <span
                                                            className={showPassword ? "svg icon-20 icon-visible-on" : "svg icon-20 icon-visible-off"}
                                                            onClick={() => setShowPassword(!showPassword)}
                                                        />
                                                    ) : (
                                                        <span
                                                            onClick={() => {
                                                                setIsPassEnabled(true);
                                                                setIsPasswordUpdated(true);
                                                                setPassword("");
                                                            }}
                                                        >
                                                            {" "}
                                                            {_("btn.reset_password")}{" "}
                                                        </span>
                                                    )}
                                                </div>
                                                {meta.error && meta.dirty && <span className="form-error">{meta.error}</span>}
                                            </div>
                                        )}
                                    </Field>
                                </div>
                                <div className="formField">
                                    <div className="fieldLabel"> {_("label.clip_link(url)")} </div>
                                    <Field name="link" type="text" value={clipLinkState} initialValue={clipLinkState}>
                                        {({ input, meta }) => <input {...input} disabled />}
                                    </Field>
                                </div>
                                <div className="formField">
                                    <div className="fieldLabel">{_("match.placeholder.description")}</div>
                                    <Field name="description" type="text">
                                        {({ input, meta }) => <textarea {...input} />}
                                    </Field>
                                </div>
                                <div className="formButtons">
                                    <button type="submit">{clipLinkState ? _("button.update_link") : _("button.generate_link")}</button>
                                    {clipLinkState && (
                                        <button
                                            className="copy"
                                            type="button"
                                            onClick={() => {
                                                navigator.clipboard.writeText(clipLinkState);
                                                toastMessage("success", _("message.link_copied_to_clipboard"));
                                            }}
                                        >
                                            {_("label.copy_link")}
                                        </button>
                                    )}
                                </div>
                            </form>
                        );
                    }}
                />
            </Modal>
        </div>
    );
}
